import React, { useMemo, useState } from 'react';

import { Helmet } from 'react-helmet';
import { useSearchParams } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';

import { type MarketingRouterRuleRecord, useMarketingRouterRules } from '~/hooks/apis/promo/useMarketingRouterRules';
import { useMarketingRouterSignificances } from '~/hooks/apis/promo/useMarketingRouterSignificance';
import useCurrentTenant from '~/hooks/useCurrentTenant';

import type { MarketingRouterInternalQuery } from '~/services/PromoService';

import { isoToSydneyDate } from '~/utils/dateFormatter';

import MarketingRouterComparisonConfidenceCell from './MarketingRouterComparisonConfidenceCell';
import MarketingRouterComparisonLiftCell from './MarketingRouterComparisonLiftCell';
import MarketingRouterTermLabel from './MarketingRouterTermLabel';
import {
  type ComparisonEntry,
  MARKETING_ROUTER_RULES_PATH,
  MIN_COMPARED_RULES,
  formatDateRange,
  getComparableMetrics,
  getComparisonDefinitionRows,
  getComparisonDifferences,
  getComparisonVariantRows,
  getDefaultComparisonMetric,
  getMissingMetricReason,
  getUnreliableComparedRuleNames,
  isComparedRuleUnreliable,
  parseComparisonRuleIds,
  toRulesListPath,
} from './marketingRouterComparison';
import { MARKETING_ROUTER_STATUS_STYLE, getMarketingRouterRuleStatus } from './marketingRouterRuleFilters';
import {
  METRIC_DISPLAY,
  type MarketingRouterMetricKey,
  formatMetricTotal,
  formatMetricValue,
  formatVisitors,
  toLoyaltyOfferTypesQuery,
} from './marketingRouterSignificanceDisplay';

/** Columns in the results table, so a grouping row can span all of them. */
const RESULT_COLUMN_COUNT = 6;

function getRuleResultsPath(marketingRouterId: string): string {
  return `/marketing/marketing-router-rules/${encodeURIComponent(marketingRouterId)}`;
}

/**
 * One rule's block of the results table: a heading row naming the rule, then
 * its control and each of its treatments. Nothing crosses the block boundary —
 * every lift and confidence is the one svc-promo reported for that rule
 * against that rule's own control.
 */
function RuleResultRows({
  entry,
  metric,
  isLoading,
  isError,
  today,
}: {
  entry: ComparisonEntry;
  metric: MarketingRouterMetricKey;
  isLoading: boolean;
  isError: boolean;
  /** Today as a Sydney calendar day, the same footing the rule dates are on. */
  today: string;
}) {
  const { rule, significance } = entry;
  const rows = significance ? getComparisonVariantRows(significance, metric) : [];
  const status = getMarketingRouterRuleStatus(rule, today);
  const isUnreliable = isComparedRuleUnreliable(entry);
  const warnings = significance?.health.warnings ?? [];

  return (
    <>
      <TableRow>
        <TableCell colSpan={RESULT_COLUMN_COUNT} sx={{ bgcolor: 'action.hover' }}>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <MuiLink component={Link} to={getRuleResultsPath(rule.marketingRouterId)} underline="hover">
              <Typography variant="subtitle2" component="span">
                {rule.name}
              </Typography>
            </MuiLink>
            <Chip label={status} color={MARKETING_ROUTER_STATUS_STYLE[status].chipColor} size="small" />
            {isUnreliable ? <Chip label="Traffic split broken" color="error" size="small" /> : null}
            <Typography variant="caption" color="text.secondary">
              {formatDateRange(rule.start_date, rule.end_date)}
            </Typography>
          </Stack>
        </TableCell>
      </TableRow>
      {isUnreliable ? (
        <TableRow>
          <TableCell colSpan={RESULT_COLUMN_COUNT}>
            <Alert severity="error">
              Traffic is not splitting between “{rule.name}”'s variants the way the rule is configured, so every number
              in this block is unreliable.
            </Alert>
          </TableCell>
        </TableRow>
      ) : null}
      {warnings.map((warning) => (
        <TableRow key={`${rule.id}-${warning}`}>
          <TableCell colSpan={RESULT_COLUMN_COUNT}>
            <Alert severity="warning">{warning}</Alert>
          </TableCell>
        </TableRow>
      ))}
      {isLoading ? (
        <TableRow>
          <TableCell colSpan={RESULT_COLUMN_COUNT}>
            <Typography color="text.secondary" variant="body2">
              Loading results…
            </Typography>
          </TableCell>
        </TableRow>
      ) : null}
      {isError ? (
        <TableRow>
          <TableCell colSpan={RESULT_COLUMN_COUNT}>
            <Alert severity="error">Results for “{rule.name}” could not be loaded.</Alert>
          </TableCell>
        </TableRow>
      ) : null}
      {!isLoading && !isError && rows.length === 0 ? (
        <TableRow>
          <TableCell colSpan={RESULT_COLUMN_COUNT}>
            <Typography color="text.secondary" variant="body2">
              {getMissingMetricReason(entry, metric)}
            </Typography>
          </TableCell>
        </TableRow>
      ) : null}
      {rows.map((row) => (
        <TableRow key={`${rule.id}-${row.variantId}`} hover>
          <TableCell>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2">{row.variantName}</Typography>
              {row.isControl ? <Chip label="Control" size="small" variant="outlined" /> : null}
            </Stack>
          </TableCell>
          <TableCell align="right">{formatVisitors(row.visitors)}</TableCell>
          <TableCell align="right">{formatMetricValue(metric, row.value)}</TableCell>
          <TableCell align="right">{formatMetricTotal(metric, row.total)}</TableCell>
          <TableCell>
            <MarketingRouterComparisonLiftCell row={row} />
          </TableCell>
          <TableCell>
            <MarketingRouterComparisonConfidenceCell row={row} />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

function ComparisonSetupTable({ entries }: { entries: Array<ComparisonEntry> }) {
  const rows = useMemo(() => getComparisonDefinitionRows(entries), [entries]);

  return (
    <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Table size="small" aria-label="Rule setup comparison">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '16%' }} />
            {entries.map((entry) => (
              <TableCell key={entry.rule.id}>
                <MuiLink component={Link} to={getRuleResultsPath(entry.rule.marketingRouterId)} underline="hover">
                  {entry.rule.name}
                </MuiLink>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.label} sx={row.differs ? { bgcolor: 'action.hover' } : undefined}>
              <TableCell sx={{ fontWeight: 600 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {row.label}
                  </Typography>
                  {row.differs ? <Chip label="Differs" size="small" color="warning" variant="outlined" /> : null}
                </Stack>
              </TableCell>
              {row.values.map((value, index) => (
                <TableCell key={entries[index].rule.id}>
                  <Typography variant="body2">{value}</Typography>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function MarketingRouterRuleComparisonPage() {
  const { tenant } = useCurrentTenant();
  const [searchParams] = useSearchParams();
  const ruleIds = useMemo(() => parseComparisonRuleIds(searchParams), [searchParams]);

  const query: MarketingRouterInternalQuery = {
    brand: tenant.brand as MarketingRouterInternalQuery['brand'],
    page: 0,
    pageSize: 100,
  };
  const rulesQuery = useMarketingRouterRules({ query });

  // Selection order is the column order, so the rules are looked up by the
  // ids in the URL rather than filtered out of the list's own order.
  const rules = useMemo(() => {
    const byId = new Map((rulesQuery.data ?? []).map((rule) => [rule.id, rule]));

    return ruleIds
      .map((ruleId) => byId.get(ruleId))
      .filter((rule): rule is MarketingRouterRuleRecord => rule !== undefined);
  }, [rulesQuery.data, ruleIds]);
  const missingRuleIds = useMemo(
    () => (rulesQuery.isSuccess ? ruleIds.filter((ruleId) => !rules.some((rule) => rule.id === ruleId)) : []),
    [rulesQuery.isSuccess, ruleIds, rules],
  );

  // Every rule is counted over every product, so no rule is narrowed to a
  // scope the others are not. The detail page is where a scope is chosen.
  const significanceQueries = useMarketingRouterSignificances({
    rules: rules.map((rule) => ({ marketingRouterId: rule.marketingRouterId, ruleId: rule.id })),
    loyaltyOfferTypes: toLoyaltyOfferTypesQuery([]),
  });

  const entries = useMemo<Array<ComparisonEntry>>(
    () => rules.map((rule, index) => ({ rule, significance: significanceQueries[index]?.data })),
    [rules, significanceQueries],
  );
  const metrics = useMemo(() => getComparableMetrics(entries), [entries]);
  const differences = useMemo(() => getComparisonDifferences(entries), [entries]);
  const unreliableRuleNames = useMemo(() => getUnreliableComparedRuleNames(entries), [entries]);

  // Left unset until the operator picks one, so the page follows the first
  // rule's primary metric as results arrive instead of sticking on a guess
  // made before anything had loaded.
  const [chosenMetric, setChosenMetric] = useState<MarketingRouterMetricKey | null>(null);
  const defaultMetric = useMemo(() => getDefaultComparisonMetric(entries), [entries]);
  const metric = chosenMetric !== null && metrics.includes(chosenMetric) ? chosenMetric : defaultMetric;

  // Rule dates are Sydney calendar days, so "today" has to be one too —
  // otherwise a rule reads as Running here and Ended on the rules list.
  const today = useMemo(() => isoToSydneyDate(new Date().toISOString()), []);
  const hasEnoughRuleIds = ruleIds.length >= MIN_COMPARED_RULES;
  // Gated on the rules that actually loaded, not the ids in the URL: a shared
  // link whose rules were deleted or belong to another tenant must not draw a
  // comparison of one rule against nothing.
  const canCompare = rules.length >= MIN_COMPARED_RULES;
  const hasTooFewLoadedRules = hasEnoughRuleIds && rulesQuery.isSuccess && !canCompare;

  return (
    <>
      <Helmet>
        <title>Marketing / Router Rules / Compare</title>
      </Helmet>
      <Container>
        <PageHeader title="Compare rule results">
          <Button component={Link} to={toRulesListPath(ruleIds)} variant="text">
            Back to rules
          </Button>
        </PageHeader>

        {!hasEnoughRuleIds ? (
          <Alert severity="info">
            Select at least {MIN_COMPARED_RULES} rules on the{' '}
            <MuiLink component={Link} to={MARKETING_ROUTER_RULES_PATH} underline="hover">
              rules list
            </MuiLink>{' '}
            and choose Compare results.
          </Alert>
        ) : null}
        {rulesQuery.isLoading ? <Typography color="text.secondary">Loading rules…</Typography> : null}
        {rulesQuery.isError ? (
          <Alert severity="error">
            {rulesQuery.error instanceof Error ? rulesQuery.error.message : 'Failed to load marketing router rules.'}
          </Alert>
        ) : null}
        {missingRuleIds.length > 0 ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {missingRuleIds.length === 1 ? 'Rule' : 'Rules'} {missingRuleIds.join(', ')}{' '}
            {missingRuleIds.length === 1 ? 'was' : 'were'} not found for the current tenant and{' '}
            {missingRuleIds.length === 1 ? 'is' : 'are'} not compared.
          </Alert>
        ) : null}
        {hasTooFewLoadedRules ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Fewer than {MIN_COMPARED_RULES} of the selected rules could be loaded, so there is nothing to compare.{' '}
            <MuiLink component={Link} to={toRulesListPath(ruleIds)} underline="hover">
              Pick another rule
            </MuiLink>{' '}
            to compare against.
          </Alert>
        ) : null}

        {canCompare ? (
          <Stack spacing={4}>
            {unreliableRuleNames.length > 0 ? (
              <Alert severity="error">
                <AlertTitle>
                  {unreliableRuleNames.length === 1 ? 'One rule’s results' : 'Some rules’ results'} cannot be trusted
                </AlertTitle>
                <Typography variant="body2">
                  {unreliableRuleNames.join(', ')} failed the sample-ratio check: traffic is not splitting between
                  variants the way the {unreliableRuleNames.length === 1 ? 'rule is' : 'rules are'} configured. Check
                  the setup and targeting before reading anything into{' '}
                  {unreliableRuleNames.length === 1 ? 'its' : 'their'} numbers below.
                </Typography>
              </Alert>
            ) : null}
            {differences.length > 0 ? (
              <Alert severity="info">
                <AlertTitle>These rules were not set up the same way</AlertTitle>
                <Typography variant="body2">
                  They differ on {differences.join(', ')}. Each rule is still measured against its own control, so the
                  results below are comparable as separate experiments — but a difference in audience, dates or metrics
                  is a reason for the numbers to differ on its own.
                </Typography>
              </Alert>
            ) : null}

            <Stack spacing={1.5}>
              <Typography variant="h2">Setup</Typography>
              <ComparisonSetupTable entries={entries} />
            </Stack>

            <Stack spacing={1.5}>
              <Typography variant="h2">Results</Typography>
              {metrics.length > 0 && metric ? (
                <>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={metric}
                    onChange={(_, next: MarketingRouterMetricKey | null) => {
                      if (next) {
                        setChosenMetric(next);
                      }
                    }}
                    aria-label="Metric to compare"
                  >
                    {metrics.map((key) => (
                      <ToggleButton key={key} value={key}>
                        {METRIC_DISPLAY[key].name}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  <Card>
                    <CardContent>
                      <TableContainer>
                        <Table size="small" aria-label={`${METRIC_DISPLAY[metric].name} comparison`}>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ width: '22%' }}>Variant</TableCell>
                              <TableCell align="right">Visitors</TableCell>
                              <TableCell align="right">{METRIC_DISPLAY[metric].perVisitorLabel}</TableCell>
                              <TableCell align="right">{METRIC_DISPLAY[metric].totalLabel}</TableCell>
                              <TableCell sx={{ width: '16%' }}>Lift vs control</TableCell>
                              <TableCell sx={{ width: '18%' }}>
                                <MarketingRouterTermLabel term="confidence">Confidence</MarketingRouterTermLabel>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {entries.map((entry, index) => (
                              <RuleResultRows
                                key={entry.rule.id}
                                entry={entry}
                                metric={metric}
                                isLoading={significanceQueries[index]?.isLoading ?? false}
                                isError={significanceQueries[index]?.isError ?? false}
                                today={today}
                              />
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <Box sx={{ mt: 1.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          Every lift and confidence is measured against that rule's own control. Results are shown side
                          by side, never pooled or recalculated across rules.
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </>
              ) : (
                <Typography color="text.secondary">
                  {significanceQueries.some((significanceQuery) => significanceQuery.isLoading)
                    ? 'Loading results…'
                    : 'None of the selected rules have reported results yet.'}
                </Typography>
              )}
            </Stack>
          </Stack>
        ) : null}
      </Container>
    </>
  );
}
